/**
 * Client-side gateway to the server-side Claude proxy callable.
 *
 * Every client AI feature routes through here instead of calling
 * `https://api.anthropic.com` directly. This keeps the Anthropic API key on
 * the server (behind the `claudeProxy` Cloud Function secret) so it no longer
 * ships in the browser bundle via `VITE_ANTHROPIC_API_KEY`.
 *
 * Faithful 1:1 replacement for the old `fetch(...)` shape the services used:
 * a system prompt + a single user message in, the assistant text out.
 *
 * @see functions/src/ai/claudeProxy.ts (server)
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '@/firebase';

export interface CallClaudeRequest {
  /** Model id (use the CLAUDE_MODEL_* constants from ai-config). Defaults to Sonnet server-side. */
  model?: string;
  /** Response token budget (server clamps to 8192). */
  maxTokens: number;
  /** Optional system prompt (plain text). */
  system?: string;
  /** When true the system prompt is sent with ephemeral prompt-caching. */
  cacheSystem?: boolean;
  /** Optional sampling temperature (0–1). */
  temperature?: number;
  /** The single user message content (plain text). */
  userContent: string;
}

export interface CallClaudeResult {
  text: string;
  stopReason?: string;
}

interface ProxyInput {
  model?: string;
  maxTokens: number;
  system?: string;
  cacheSystem?: boolean;
  temperature?: number;
  userContent: string;
}

// Lazily bind the callable on first use. Building it at module load would
// touch the Firebase functions instance on import, which couples every
// service that imports this helper to a live Firebase init (and breaks unit
// tests that merely import a migrated service).
let proxyRef: ReturnType<typeof httpsCallable<ProxyInput, CallClaudeResult>> | null = null;
function getProxy() {
  if (!proxyRef) {
    proxyRef = httpsCallable<ProxyInput, CallClaudeResult>(functions, 'claudeProxy');
  }
  return proxyRef;
}

/**
 * Run a one-shot Claude completion via the server proxy.
 *
 * Throws on transport / auth / API errors (the underlying callable surfaces
 * HttpsError codes such as `resource-exhausted` for rate limits and
 * `deadline-exceeded` for timeouts). Callers keep whatever retry / fallback
 * policy they already had around the old fetch call.
 */
export async function callClaudeViaProxy(
  req: CallClaudeRequest,
): Promise<CallClaudeResult> {
  const res = await getProxy()({
    model: req.model,
    maxTokens: req.maxTokens,
    system: req.system,
    cacheSystem: req.cacheSystem,
    temperature: req.temperature,
    userContent: req.userContent,
  });
  return { text: res.data?.text ?? '', stopReason: res.data?.stopReason };
}
